import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Lista padronizada de idiomas disponíveis
const AVAILABLE_LANGUAGES = ['Português', 'Inglês', 'Espanhol', 'Francês', 'Italiano', 'Alemão', 'Mandarim', 'Japonês', 'Russo', 'Árabe', 'Hindi', 'Coreano', 'Holandês', 'Sueco', 'Norueguês'];
interface LanguageSelectProps {
  selectedLanguages: string[];
  onLanguagesChange: (languages: string[]) => void;
  maxLanguages?: number;
}
const LanguageSelect = ({
  selectedLanguages = [],
  onLanguagesChange,
  maxLanguages = 6
}: LanguageSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Garantir que selectedLanguages seja sempre um array válido
  const safeSelectedLanguages = React.useMemo(() => {
    if (!Array.isArray(selectedLanguages)) {
      console.warn('LanguageSelect: selectedLanguages is not an array, defaulting to empty array');
      return [];
    }
    return selectedLanguages.filter(item => item && typeof item === 'string' && item.trim() !== '');
  }, [selectedLanguages]);

  // Idiomas disponíveis para seleção (excluindo os já selecionados)
  const availableLanguages = React.useMemo(() => {
    return AVAILABLE_LANGUAGES.filter(language => !safeSelectedLanguages.includes(language));
  }, [safeSelectedLanguages]);

  // Filtrar idiomas baseado no termo de busca
  const filteredLanguages = React.useMemo(() => {
    if (!searchTerm || typeof searchTerm !== 'string') {
      return availableLanguages;
    }
    return availableLanguages.filter(language => language.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [availableLanguages, searchTerm]);
  const handleLanguageSelect = (language: string) => {
    if (!language || typeof language !== 'string' || language.trim() === '') {
      return;
    }
    if (safeSelectedLanguages.includes(language)) {
      return;
    }
    if (safeSelectedLanguages.length >= maxLanguages) {
      return;
    }
    const newLanguages = [...safeSelectedLanguages, language];
    onLanguagesChange(newLanguages);
    setOpen(false);
    setSearchTerm('');
  };
  const handleLanguageRemove = (languageToRemove: string) => {
    if (!languageToRemove || typeof languageToRemove !== 'string') {
      return;
    }
    const newLanguages = safeSelectedLanguages.filter(language => language !== languageToRemove);
    onLanguagesChange(newLanguages);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(typeof value === 'string' ? value : '');
  };
  return <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label htmlFor="languages">Fluente em:</Label>
        <span className="text-sm text-muted-foreground">
          {safeSelectedLanguages.length}/{maxLanguages} idiomas selecionados
        </span>
      </div>

      <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-3 border rounded-md">
        {safeSelectedLanguages.map(language => <Badge key={`selected-${language}`} variant="secondary" className="flex items-center gap-1">
            {language}
            <button type="button" onClick={() => handleLanguageRemove(language)} className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5" aria-label={`Remover ${language}`}>
              <X className="h-3 w-3" />
            </button>
          </Badge>)}
        
        {safeSelectedLanguages.length < maxLanguages && <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-6 px-2 border-dashed" type="button">
                <Plus className="h-3 w-3 mr-1" />
                Adicionar
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0">
              <div className="p-3 border-b">
                <div className="flex items-center">
                  <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <Input placeholder="Buscar idioma..." value={searchTerm} onChange={handleSearchChange} className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto p-1">
                {filteredLanguages.length === 0 ? <div className="py-6 text-center text-sm text-muted-foreground">
                    {searchTerm ? 'Nenhum idioma encontrado.' : 'Todos os idiomas já foram selecionados.'}
                  </div> : filteredLanguages.map(language => <button key={`available-${language}`} type="button" className="w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer" onClick={() => handleLanguageSelect(language)}>
                      {language}
                    </button>)}
              </div>
            </PopoverContent>
          </Popover>}
      </div>

      {safeSelectedLanguages.length >= maxLanguages && <p className="text-sm text-muted-foreground">
          Limite máximo de {maxLanguages} idiomas atingido.
        </p>}
    </div>;
};
export default LanguageSelect;